import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Mail, Phone, Calendar, CheckCircle2, XCircle,
  FileText, DollarSign, TrendingUp, Clock, Trash2
} from 'lucide-react';
import { AdminLayout } from './AdminLayout';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { getLead, updateLead, deleteLead } from '@/lib/api';

const InfoCard = ({ icon: Icon, label, value, highlight }) => (
  <div className="p-4 bg-obsidian border border-white/5">
    <div className="flex items-center gap-2 text-white/40 text-sm mb-1">
      <Icon className="w-4 h-4" />
      <span>{label}</span>
    </div>
    <p className={`font-medium ${highlight ? 'text-rtb-purple' : ''}`}>
      {value || '-'}
    </p>
  </div>
);

export default function AdminLeadDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState('');

  useEffect(() => {
    const fetchLead = async () => {
      try {
        const data = await getLead(id);
        setLead(data);
        setNotes(data.notes || '');
        setStatus(data.status);
      } catch (error) {
        console.error('Failed to fetch lead:', error);
        toast.error('Failed to load lead details');
        navigate('/admin/leads');
      } finally {
        setLoading(false);
      }
    };
    fetchLead();
  }, [id, navigate]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await updateLead(id, { notes, status });
      setLead(updated);
      toast.success('Lead updated successfully');
    } catch (error) {
      toast.error('Failed to update lead');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this lead? This action cannot be undone.')) {
      return;
    }
    
    try {
      await deleteLead(id);
      toast.success('Lead deleted');
      navigate('/admin/leads');
    } catch (error) {
      toast.error('Failed to delete lead');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const capitalMap = {
    'under_1000': 'Under $1,000',
    '1000_2500': '$1,000 - $2,500',
    '2500_5000': '$2,500 - $5,000',
    '5000_10000': '$5,000 - $10,000',
    'over_10000': 'Over $10,000'
  };

  if (loading) {
    return (
      <AdminLayout title="Lead Details">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-rtb-purple border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  if (!lead) return null;

  return (
    <AdminLayout title="Lead Details">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => navigate('/admin/leads')}
            className="text-white/40 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="w-16 h-16 bg-rtb-purple/10 rounded-full flex items-center justify-center">
            <span className="text-rtb-purple text-2xl font-bold">
              {lead.first_name?.[0]}{lead.last_name?.[0]}
            </span>
          </div>
          <div>
            <h2 className="text-2xl font-oswald font-bold">
              {lead.first_name} {lead.last_name}
            </h2>
            <div className="flex items-center gap-4 text-white/60 text-sm">
              <span className="flex items-center gap-1">
                <Mail className="w-4 h-4" /> {lead.email}
              </span>
              <span className="flex items-center gap-1">
                <Phone className="w-4 h-4" /> {lead.phone}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4">
          {lead.is_qualified ? (
            <span className="flex items-center gap-2 px-4 py-2 bg-green-500/10 text-green-400">
              <CheckCircle2 className="w-5 h-5" /> Qualified
            </span>
          ) : (
            <span className="flex items-center gap-2 px-4 py-2 bg-red-500/10 text-red-400">
              <XCircle className="w-5 h-5" /> Not Qualified
            </span>
          )}
          <Button
            variant="destructive"
            onClick={handleDelete}
            className="bg-signal-red/10 text-signal-red hover:bg-signal-red hover:text-white"
          >
            <Trash2 className="w-4 h-4 mr-2" /> Delete
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <InfoCard 
              icon={TrendingUp} 
              label="Score" 
              value={lead.qualification_score}
              highlight
            />
            <InfoCard 
              icon={DollarSign} 
              label="Capital" 
              value={capitalMap[lead.qualification?.available_capital]}
              highlight={['2500_5000', '5000_10000', 'over_10000'].includes(lead.qualification?.available_capital)}
            />
            <InfoCard 
              icon={Clock} 
              label="Timeline" 
              value={lead.qualification?.investment_timeline?.replace(/-/g, ' ')}
            />
            <InfoCard 
              icon={Calendar} 
              label="Call Booked" 
              value={lead.calendly_booked ? 'Yes' : 'No'}
            />
          </div>

          {/* Qualification Details */}
          <div className="p-6 bg-charcoal border border-white/10">
            <h3 className="font-oswald text-lg font-bold mb-4">Qualification Details</h3>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <p className="text-white/40 text-sm mb-1">Experience Level</p>
                <p className="capitalize">{lead.qualification?.investment_experience || '-'}</p>
              </div>
              <div>
                <p className="text-white/40 text-sm mb-1">Credit Score Range</p>
                <p>{lead.qualification?.credit_score_range || '-'}</p>
              </div>
              <div className="md:col-span-2">
                <p className="text-white/40 text-sm mb-2">Investment Goals</p>
                <div className="flex flex-wrap gap-2">
                  {lead.qualification?.investment_goals?.map((goal, i) => (
                    <span 
                      key={i}
                      className="px-3 py-1 bg-rtb-purple/10 text-rtb-purple text-sm"
                    >
                      {goal.replace(/_/g, ' ')}
                    </span>
                  )) || <span className="text-white/40">-</span>}
                </div>
              </div>
            </div>
          </div>

          {/* Verification Details */}
          <div className="p-6 bg-charcoal border border-white/10">
            <h3 className="font-oswald text-lg font-bold mb-4">Verification</h3>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-obsidian">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className={`w-5 h-5 ${lead.verification?.self_declaration ? 'text-green-400' : 'text-white/20'}`} />
                  <span>Self-Declaration</span>
                </div>
                <span className={lead.verification?.self_declaration ? 'text-green-400' : 'text-white/40'}>
                  {lead.verification?.self_declaration ? 'Completed' : 'Not completed'}
                </span>
              </div>

              <div className="flex items-center justify-between p-4 bg-obsidian">
                <div className="flex items-center gap-3">
                  <FileText className={`w-5 h-5 ${lead.verification?.has_bank_statement ? 'text-green-400' : 'text-white/20'}`} />
                  <span>Bank Statement</span>
                </div>
                <span className={lead.verification?.has_bank_statement ? 'text-green-400' : 'text-white/40'}>
                  {lead.verification?.bank_statement_filename || 'Not uploaded'}
                </span>
              </div>

              <div className="flex items-center justify-between p-4 bg-obsidian">
                <div className="flex items-center gap-3">
                  <DollarSign className={`w-5 h-5 ${lead.verification?.stripe_deposit ? 'text-green-400' : 'text-white/20'}`} />
                  <span>Stripe Deposit</span>
                </div>
                <span className="text-white/40">
                  {lead.verification?.stripe_deposit ? `$${lead.verification.stripe_deposit_amount}` : 'Not completed'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Status & Notes */}
          <div className="p-6 bg-charcoal border border-white/10">
            <h3 className="font-oswald text-lg font-bold mb-4">Manage Lead</h3>
            
            <div className="space-y-4">
              <div>
                <Label className="text-white/60 mb-2 block">Status</Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger className="input-dark">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-charcoal border-white/10">
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="qualified">Qualified</SelectItem>
                    <SelectItem value="unqualified">Unqualified</SelectItem>
                    <SelectItem value="contacted">Contacted</SelectItem>
                    <SelectItem value="converted">Converted</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-white/60 mb-2 block">Notes</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="input-dark min-h-[150px]"
                  placeholder="Add notes about this lead..."
                  data-testid="notes-textarea"
                />
              </div>

              <Button 
                onClick={handleSave}
                disabled={saving}
                className="btn-primary w-full"
                data-testid="save-lead-btn"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>

          {/* Timeline */}
          <div className="p-6 bg-charcoal border border-white/10">
            <h3 className="font-oswald text-lg font-bold mb-4">Activity</h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-rtb-purple rounded-full mt-2" />
                <div>
                  <p className="text-sm">Lead created</p>
                  <p className="text-white/40 text-xs">{formatDate(lead.created_at)}</p>
                </div>
              </div>
              {lead.updated_at !== lead.created_at && (
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-rtb-green rounded-full mt-2" />
                  <div>
                    <p className="text-sm">Last updated</p>
                    <p className="text-white/40 text-xs">{formatDate(lead.updated_at)}</p>
                  </div>
                </div>
              )}
              {lead.calendly_booked && (
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-green-400 rounded-full mt-2" />
                  <div>
                    <p className="text-sm">Calendly call booked</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
